"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Pair = void 0;
const tslib_1 = require("tslib");
const price_1 = require("./fractions/price");
const tokenAmount_1 = require("./fractions/tokenAmount");
const tiny_invariant_1 = tslib_1.__importDefault(require("tiny-invariant"));
const jsbi_1 = tslib_1.__importDefault(require("jsbi"));
const solidity_1 = require("@ethersproject/solidity");
const address_1 = require("@ethersproject/address");
const constants_1 = require("../constants");
const utils_1 = require("../utils");
const errors_1 = require("../errors");
const token_1 = require("./token");
const contracts_1 = require("@ethersproject/contracts");
const providers_1 = require("@ethersproject/providers");
const networks_1 = require("@ethersproject/networks");
const IUniswapV2Factory_json_1 = tslib_1.__importDefault(require("@uniswap/v2-core/build/IUniswapV2Factory.json"));
let PAIR_ADDRESS_CACHE = {};
class Pair {
  static getAddress(dexName, factoryAddress, initCodeHash, tokenA, tokenB) {
    const tokens = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]; // does safety checks
    const chainId = tokens[0].chainId;
    if (PAIR_ADDRESS_CACHE?.[chainId] === undefined) {
      PAIR_ADDRESS_CACHE[chainId] = {};
    }
    if (PAIR_ADDRESS_CACHE?.[chainId]?.[dexName]?.[tokens[0].address]?.[tokens[1].address] === undefined) {
      PAIR_ADDRESS_CACHE[chainId][dexName] = {
        ...PAIR_ADDRESS_CACHE[chainId][dexName],
        [tokens[0].address]: {
          ...PAIR_ADDRESS_CACHE[chainId][dexName]?.[tokens[0].address],
          [tokens[1].address]: (0, address_1.getCreate2Address)(factoryAddress, (0, solidity_1.keccak256)(['bytes'], [(0, solidity_1.pack)(['address', 'address'], [tokens[0].address, tokens[1].address])]), initCodeHash)
        }
      };
    }
    return PAIR_ADDRESS_CACHE[chainId][dexName][tokens[0].address][tokens[1].address];
  }
  static async getAddressByCallingFactory(dexName, factoryAddress, tokenA, tokenB, provider = (0, providers_1.getDefaultProvider)((0, networks_1.getNetwork)(tokenA.chainId))) {
    const tokens = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]; // does safety checks
    const chainId = tokens[0].chainId;
    if (PAIR_ADDRESS_CACHE?.[chainId] === undefined) {
      PAIR_ADDRESS_CACHE[chainId] = {};
    }
    if (PAIR_ADDRESS_CACHE?.[chainId]?.[dexName]?.[tokens[0].address]?.[tokens[1].address] === undefined) {
      const pairAddress = await new contracts_1.Contract(factoryAddress, IUniswapV2Factory_json_1.default.abi, provider).getPair(tokens[0].address, tokens[1].address);
      PAIR_ADDRESS_CACHE[chainId][dexName] = {
        ...PAIR_ADDRESS_CACHE[chainId][dexName],
        [tokens[0].address]: {
          ...PAIR_ADDRESS_CACHE[chainId][dexName]?.[tokens[0].address],
          [tokens[1].address]: pairAddress
        }
      };
    }
    return PAIR_ADDRESS_CACHE[chainId][dexName][tokens[0].address][tokens[1].address];
  }
  constructor(dexName, factoryAddress, initCodeHash, tokenAmountA, tokenAmountB, liquidityToken) {
    this.dexName = dexName;
    this.factoryAddress = factoryAddress;
    this.initCodeHash = initCodeHash;
    const tokenAmounts = tokenAmountA.token.sortsBefore(tokenAmountB.token) // does safety checks
    ? [tokenAmountA, tokenAmountB] : [tokenAmountB, tokenAmountA];
    this.liquidityToken = liquidityToken ?? new token_1.Token(tokenAmounts[0].token.chainId, Pair.getAddress(dexName, factoryAddress, initCodeHash, tokenAmounts[0].token, tokenAmounts[1].token), 18, 'V2', dexName);
    this.tokenAmounts = tokenAmounts;
  }
  /**
   * Returns true if the token is either token0 or token1
   * @param token to check
   */
  involvesToken(token) {
    return token.equals(this.token0) || token.equals(this.token1);
  }
  /**
   * Returns the current mid price of the pair in terms of token0, i.e. the ratio of reserve1 to reserve0
   */
  get token0Price() {
    return new price_1.Price(this.token0, this.token1, this.tokenAmounts[0].raw, this.tokenAmounts[1].raw);
  }
  /**
   * Returns the current mid price of the pair in terms of token1, i.e. the ratio of reserve0 to reserve1
   */
  get token1Price() {
    return new price_1.Price(this.token1, this.token0, this.tokenAmounts[1].raw, this.tokenAmounts[0].raw);
  }
  /**
   * Return the price of the given token in terms of the other token in the pair.
   * @param token token to return price of
   */
  priceOf(token) {
    (0, tiny_invariant_1.default)(this.involvesToken(token), 'TOKEN');
    return token.equals(this.token0) ? this.token0Price : this.token1Price;
  }
  /**
   * Returns the chain ID of the tokens in the pair.
   */
  get chainId() {
    return this.token0.chainId;
  }
  get token0() {
    return this.tokenAmounts[0].token;
  }
  get token1() {
    return this.tokenAmounts[1].token;
  }
  get reserve0() {
    return this.tokenAmounts[0];
  }
  get reserve1() {
    return this.tokenAmounts[1];
  }
  reserveOf(token) {
    (0, tiny_invariant_1.default)(this.involvesToken(token), 'TOKEN');
    return token.equals(this.token0) ? this.reserve0 : this.reserve1;
  }
  getOutputAmount(inputAmount) {
    (0, tiny_invariant_1.default)(this.involvesToken(inputAmount.token), 'TOKEN');
    if (jsbi_1.default.equal(this.reserve0.raw, constants_1.ZERO) || jsbi_1.default.equal(this.reserve1.raw, constants_1.ZERO)) {
      throw new errors_1.InsufficientReservesError();
    }
    const inputReserve = this.reserveOf(inputAmount.token);
    const outputReserve = this.reserveOf(inputAmount.token.equals(this.token0) ? this.token1 : this.token0);
    const inputAmountWithFee = jsbi_1.default.multiply(inputAmount.raw, constants_1._997);
    const numerator = jsbi_1.default.multiply(inputAmountWithFee, outputReserve.raw);
    const denominator = jsbi_1.default.add(jsbi_1.default.multiply(inputReserve.raw, constants_1._1000), inputAmountWithFee);
    const outputAmount = new tokenAmount_1.TokenAmount(inputAmount.token.equals(this.token0) ? this.token1 : this.token0, jsbi_1.default.divide(numerator, denominator));
    if (jsbi_1.default.equal(outputAmount.raw, constants_1.ZERO)) {
      throw new errors_1.InsufficientInputAmountError();
    }
    return [outputAmount, new Pair(this.dexName, this.factoryAddress, this.initCodeHash, inputReserve.add(inputAmount), outputReserve.subtract(outputAmount), this.liquidityToken)];
  }
  getInputAmount(outputAmount) {
    (0, tiny_invariant_1.default)(this.involvesToken(outputAmount.token), 'TOKEN');
    if (jsbi_1.default.equal(this.reserve0.raw, constants_1.ZERO) || jsbi_1.default.equal(this.reserve1.raw, constants_1.ZERO) || jsbi_1.default.greaterThanOrEqual(outputAmount.raw, this.reserveOf(outputAmount.token).raw)) {
      throw new errors_1.InsufficientReservesError();
    }
    const outputReserve = this.reserveOf(outputAmount.token);
    const inputReserve = this.reserveOf(outputAmount.token.equals(this.token0) ? this.token1 : this.token0);
    const numerator = jsbi_1.default.multiply(jsbi_1.default.multiply(inputReserve.raw, outputAmount.raw), constants_1._1000);
    const denominator = jsbi_1.default.multiply(jsbi_1.default.subtract(outputReserve.raw, outputAmount.raw), constants_1._997);
    const inputAmount = new tokenAmount_1.TokenAmount(outputAmount.token.equals(this.token0) ? this.token1 : this.token0, jsbi_1.default.add(jsbi_1.default.divide(numerator, denominator), constants_1.ONE));
    return [inputAmount, new Pair(this.dexName, this.factoryAddress, this.initCodeHash, inputReserve.add(inputAmount), outputReserve.subtract(outputAmount))];
  }
  getLiquidityMinted(totalSupply, tokenAmountA, tokenAmountB) {
    (0, tiny_invariant_1.default)(totalSupply.token.equals(this.liquidityToken), 'LIQUIDITY');
    const tokenAmounts = tokenAmountA.token.sortsBefore(tokenAmountB.token) // does safety checks
    ? [tokenAmountA, tokenAmountB] : [tokenAmountB, tokenAmountA];
    (0, tiny_invariant_1.default)(tokenAmounts[0].token.equals(this.token0) && tokenAmounts[1].token.equals(this.token1), 'TOKEN');
    let liquidity;
    if (jsbi_1.default.equal(totalSupply.raw, constants_1.ZERO)) {
      liquidity = jsbi_1.default.subtract((0, utils_1.sqrt)(jsbi_1.default.multiply(tokenAmounts[0].raw, tokenAmounts[1].raw)), constants_1.MINIMUM_LIQUIDITY);
    } else {
      const amount0 = jsbi_1.default.divide(jsbi_1.default.multiply(tokenAmounts[0].raw, totalSupply.raw), this.reserve0.raw);
      const amount1 = jsbi_1.default.divide(jsbi_1.default.multiply(tokenAmounts[1].raw, totalSupply.raw), this.reserve1.raw);
      liquidity = jsbi_1.default.lessThanOrEqual(amount0, amount1) ? amount0 : amount1;
    }
    if (!jsbi_1.default.greaterThan(liquidity, constants_1.ZERO)) {
      throw new errors_1.InsufficientInputAmountError();
    }
    return new tokenAmount_1.TokenAmount(this.liquidityToken, liquidity);
  }
  getLiquidityValue(token, totalSupply, liquidity, feeOn = false, kLast) {
    (0, tiny_invariant_1.default)(this.involvesToken(token), 'TOKEN');
    (0, tiny_invariant_1.default)(totalSupply.token.equals(this.liquidityToken), 'TOTAL_SUPPLY');
    (0, tiny_invariant_1.default)(liquidity.token.equals(this.liquidityToken), 'LIQUIDITY');
    (0, tiny_invariant_1.default)(jsbi_1.default.lessThanOrEqual(liquidity.raw, totalSupply.raw), 'LIQUIDITY');
    let totalSupplyAdjusted;
    if (!feeOn) {
      totalSupplyAdjusted = totalSupply;
    } else {
      (0, tiny_invariant_1.default)(!!kLast, 'K_LAST');
      const kLastParsed = (0, utils_1.parseBigintIsh)(kLast);
      if (!jsbi_1.default.equal(kLastParsed, constants_1.ZERO)) {
        const rootK = (0, utils_1.sqrt)(jsbi_1.default.multiply(this.reserve0.raw, this.reserve1.raw));
        const rootKLast = (0, utils_1.sqrt)(kLastParsed);
        if (jsbi_1.default.greaterThan(rootK, rootKLast)) {
          const numerator = jsbi_1.default.multiply(totalSupply.raw, jsbi_1.default.subtract(rootK, rootKLast));
          const denominator = jsbi_1.default.add(jsbi_1.default.multiply(rootK, constants_1.FIVE), rootKLast);
          const feeLiquidity = jsbi_1.default.divide(numerator, denominator);
          totalSupplyAdjusted = totalSupply.add(new tokenAmount_1.TokenAmount(this.liquidityToken, feeLiquidity));
        } else {
          totalSupplyAdjusted = totalSupply;
        }
      } else {
        totalSupplyAdjusted = totalSupply;
      }
    }
    return new tokenAmount_1.TokenAmount(token, jsbi_1.default.divide(jsbi_1.default.multiply(liquidity.raw, this.reserveOf(token).raw), totalSupplyAdjusted.raw));
  }
}
exports.Pair = Pair;
